import React, { FC, useMemo } from 'react';
import styles from './index.module.scss';
import mainStyles from '../index.module.scss';
import { useTranslation } from 'react-i18next';
import { useTenderData } from 'src/models/procurements/Tender/hooks';
import { InfoIcon } from 'src/common';
import { TenderSubCatName, TenderBoxMode } from 'src/models/procurements/Tender/types';
import { LotsSups, TenderBox, TenderCat, TenderSubCat } from 'src/shared';
import { isAddition } from 'src/models/procurements/Tender/helpers';
import LocationSubCat from './Location';
import TimelineSubCat from './Timeline';
import CompetitorsSubCat from './Competitors';
import ProcurementSubCat from './Procurement';
import ContractSubCat from './Contract';
import ContactSubCat from './Contact';
import LotsSubCat from './Lots';
import AdditionalSubCat from './Additional';
import CpvSubCat from './Cpv';
import classNames from 'classnames';
import Footnote from '../Footnote';
import CustomSubCat from './Custom';
import { FeatureFlag, useFeatureFlag } from 'src/helpers';

export const GeneralCat: FC = () => {
  const { t } = useTranslation();
  const { data } = useTenderData();
  const introSubCat = useMemo(() => data?.generalCat[TenderSubCatName.Intro], [data]);
  const introBoxes = useMemo(() => introSubCat?.boxes.introduction, [introSubCat]);
  const isAdditionIntro = useMemo(() => isAddition(introBoxes), [introBoxes]);
  const descSubCat = useMemo(() => data?.generalCat[TenderSubCatName.ShortDescription], [data]);
  const descBoxes = useMemo(() => descSubCat?.boxes.description, [descSubCat]);
  const isAdditionDesc = useMemo(() => isAddition(descBoxes), [descBoxes]);
  const scopeSubCat = useMemo(() => data?.generalCat[TenderSubCatName.ScopeOfContract], [data]);
  const scopeBoxes = useMemo(() => scopeSubCat?.boxes.scopeOfContract, [scopeSubCat]);
  const isAdditionScope = useMemo(() => isAddition(scopeBoxes), [scopeBoxes]);
  const editable = !!data?.isEditable;
  const isCompetitorsFeature = useFeatureFlag(FeatureFlag.Users_Competitors);

  return (
    <TenderCat>
      <div className={styles.ThreeQuarters}>
        {introSubCat && ((introBoxes && !!introBoxes.length && !introBoxes[0].isEmpty) || editable) && (
          <TenderSubCat
            subCat={introSubCat}
            actions={
              <>
                <LotsSups lots={introBoxes ? introBoxes[0].lots : []} />
                {isAdditionIntro && <InfoIcon desc={t('DetailsPage.additionAvailable')} />}
              </>
            }
          >
            <TenderBox
              box={introBoxes ? introBoxes[0] : undefined}
              title={false}
              as={TenderBoxMode.Custom}
              isFirstField
              isEditable={editable}
            />
          </TenderSubCat>
        )}
        {descSubCat && ((descBoxes && !!descBoxes.length && !descBoxes[0].isEmpty) || editable) && (
          <TenderSubCat
            subCat={descSubCat}
            actions={
              <>
                <LotsSups lots={descBoxes ? descBoxes[0].lots : []} />
                {isAdditionDesc && <InfoIcon desc={t('DetailsPage.additionAvailable')} />}
              </>
            }
          >
            <TenderBox
              box={descBoxes ? descBoxes[0] : undefined}
              title={false}
              as={TenderBoxMode.Custom}
              isFirstField
              isEditable={editable}
            />
          </TenderSubCat>
        )}
        {scopeSubCat && ((scopeBoxes && !!scopeBoxes.length && !scopeBoxes[0].isEmpty) || editable) && (
          <TenderSubCat
            subCat={scopeSubCat}
            actions={
              <>
                <LotsSups lots={scopeBoxes ? scopeBoxes[0].lots : []} />
                {isAdditionScope && <InfoIcon desc={t('DetailsPage.additionAvailable')} />}
              </>
            }
          >
            <TenderBox
              box={scopeBoxes ? scopeBoxes[0] : undefined}
              title={false}
              as={TenderBoxMode.Custom}
              isFirstField
              isEditable={editable}
            />
          </TenderSubCat>
        )}
      </div>
      <div className={styles.Quarter}>
        <LocationSubCat />
        <TimelineSubCat />
      </div>
      {!!isCompetitorsFeature && <CompetitorsSubCat />}
      <Footnote />
      <ProcurementSubCat />
      {editable ? (
        <div className={classNames(mainStyles.full, mainStyles.readOnlyBox)}>
          <ContractSubCat />
          <ContactSubCat />
        </div>
      ) : (
        <>
          <ContractSubCat />
          <ContactSubCat />
        </>
      )}
      <CustomSubCat />
      <LotsSubCat />
      <AdditionalSubCat />
      <CpvSubCat />
    </TenderCat>
  );
};

export default GeneralCat;
